import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';
import { drawFramedPane, drawStripedFabric } from './facadeDrawingPrimitives';
import { drawTimberBoards } from './propDrawingPrimitives';

export type FacadeFixtureTemplate = 'shuttered_window' | 'diamond_leaded_window' | 'striped_shop_awning' | 'scalloped_door_canopy';

/** 88×80. Open leaves flank the glazing; its opaque 24,24,16×24 patch anchors to the wall. */
function shutteredWindow(p: TownPropPalette): string {
  const shutter = (mirrored: boolean) => `<g data-part="open-shutter"${mirrored ? ' transform="translate(88 0) scale(-1 1)"' : ''}>
    <path d="M2 12L23 7V69L2 64Z" fill="${hex(p.wood)}" />
    ${drawTimberBoards({ x: 4, y: 15, width: 15, height: 47 }, p, { axis: 'horizontal', boards: 7 })}
    <path d="M3 14L21 10V65L3 62Z" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
    <path d="M5 22L20 19 M5 54L20 57" stroke="${hex(p.iron)}" stroke-width="3" />
    <circle cx="8" cy="21.5" r=".8" fill="${hex(p.stone)}" />
    <circle cx="8" cy="54.5" r=".8" fill="${hex(p.stone)}" />
    <path d="M21 17H25V24H21Z M21 53H25V60H21Z" fill="${hex(p.iron)}" />
  </g>`;
  return `<g data-part="shuttered-window">
    <path d="M24 5H64V73H24Z" fill="${hex(p.stone)}" />
    <path d="M22 2H66V7H22Z" fill="${hex(p.shade)}" />
    <path d="M25 3H63" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
    ${shutter(false)}${shutter(true)}
    <g data-part="shuttered-glazing">
      ${drawFramedPane({ x: 25, y: 7, width: 38, height: 63 }, p, { pattern: 'cross', border: 4 })}
    </g>
    <path d="M21 69H67L71 75H17Z" fill="${hex(p.woodEdge)}" />
    <path d="M17 75H71V78H17Z" fill="${hex(p.wood)}" />
    <path d="M30 3H58" stroke="${hex(p.primary)}" stroke-width="2" />
  </g>`;
}

/** 64×84. A tall pane and carved lintel retain open corners around the solid mounting patch. */
function leadedWindow(p: TownPropPalette): string {
  return `<g data-part="leaded-window">
    <path d="M7 8L32 1L57 8V77L61 80V84H3V80L7 77Z" fill="${hex(p.stone)}" />
    <path d="M7 13H10V73H7Z M54 13H57V73H54Z" fill="${hex(p.woodEdge)}" />
    <g data-part="diamond-glazing">
      ${drawFramedPane({ x: 9, y: 10, width: 46, height: 64 }, p, { pattern: 'diamond', border: 5 })}
    </g>
    <path d="M4 8L32 0L60 8V13H4Z M4 75H60V80H4Z" fill="${hex(p.wood)}" />
    <path d="M8 9L32 3L56 9 M8 77H56" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
    <path d="M27 8L32 4L37 8L32 12Z" fill="${hex(p.primary)}" />
    <path d="M30 8H34" stroke="${hex(p.glassCore)}" stroke-width="1" />
    <path d="M7 25H9 M55 48H57 M7 64H9" stroke="${hex(p.shade)}" stroke-width="1.3" />
  </g>`;
}

/** 144×64. The solid 12,4,120×8 back beam is the wall mount; cloth projects beyond it. */
function shopAwning(p: TownPropPalette): string {
  return `<g data-part="striped-shop-awning">
    <g data-part="awning-mount-beam">
      ${drawTimberBoards({ x: 12, y: 4, width: 120, height: 8 }, p, { axis: 'horizontal', boards: 1 })}
      <path d="M12 4H132V7H12Z" fill="${hex(p.woodEdge)}" />
      <circle cx="17" cy="8" r="1.5" fill="${hex(p.iron)}" />
      <circle cx="127" cy="8" r="1.5" fill="${hex(p.iron)}" />
    </g>
    <g data-part="awning-brackets" fill="none" stroke="${hex(p.iron)}" stroke-width="4" stroke-linejoin="round">
      <path d="M18 10V57L39 37 M126 10V57L105 37" />
      <path d="M18 45L29 45 M115 45H126" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
    </g>
    <path d="M10 12H134L141 43H3Z" fill="${hex(p.shade)}" />
    <g data-part="shop-awning-cloth">
      ${drawStripedFabric({ x: 4, y: 13, width: 136, height: 35 }, p, { stripes: 8, valance: 5 })}
    </g>
    <path d="M4 14H140 M5 42H139" stroke="${hex(p.woodEdge)}" stroke-width="1.5" />
    <path d="M13 13L5 41 M131 13L139 41" stroke="${hex(p.shade)}" stroke-width="1.3" />
    <path d="M17 57V62 M127 57V62" stroke="${hex(p.wood)}" stroke-width="3" />
  </g>`;
}

/** 100×52. The 10,4,80×8 beam supports a short canopy with deeper scalloped cloth. */
function doorCanopy(p: TownPropPalette): string {
  return `<g data-part="scalloped-door-canopy">
    <g data-part="canopy-mount-beam">
      ${drawTimberBoards({ x: 10, y: 4, width: 80, height: 8 }, p, { axis: 'horizontal', boards: 1 })}
      <path d="M10 4H90V7H10Z" fill="${hex(p.woodEdge)}" />
      <path d="M12 6H19V12H12Z M81 6H88V12H81Z" fill="${hex(p.iron)}" />
      <circle cx="15.5" cy="9" r="1" fill="${hex(p.stone)}" />
      <circle cx="84.5" cy="9" r="1" fill="${hex(p.stone)}" />
    </g>
    <g data-part="canopy-brackets" fill="none" stroke="${hex(p.wood)}" stroke-width="4" stroke-linejoin="round">
      <path d="M17 11V46L34 30 M83 11V46L66 30" />
      <path d="M18 40L30 30 M82 40L70 30" stroke="${hex(p.woodEdge)}" stroke-width="1.1" />
    </g>
    <path d="M10 12H90L95 31H5Z" fill="${hex(p.shade)}" />
    <g data-part="door-canopy-cloth">
      ${drawStripedFabric({ x: 5, y: 13, width: 90, height: 28 }, p, { stripes: 5, valance: 10 })}
    </g>
    <path d="M6 14H94 M6 30H94" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
    <path d="M15 45H19V49H15Z M81 45H85V49H81Z" fill="${hex(p.iron)}" />
  </g>`;
}

/** Static wall art only. Catalog mounting permissions and actual wall support belong to shared metadata. */
export function buildFacadeFixtureBody(template: FacadeFixtureTemplate, p: TownPropPalette): string {
  switch (template) {
  case 'shuttered_window': return shutteredWindow(p);
  case 'diamond_leaded_window': return leadedWindow(p);
  case 'striped_shop_awning': return shopAwning(p);
  case 'scalloped_door_canopy': return doorCanopy(p);
  }
}
